
    import __i18nConfig from '@next-translate-root/i18n'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    
import React, { useEffect, useRef, useState } from "react";
import Page from "@/src/components/Page";
import AuthShell from "@/src/layouts/AuthShell";
import AuthContent from "@/src/components/AuthContent";
import AuthCard from "@/src/components/AuthCard";
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  Checkbox,
  CloseButton,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Input,
  InputGroup,
  InputRightElement,
  Stack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  useColorMode,
  useToast,
  Heading,
  Text
} from "@chakra-ui/react";
import { ExternalLinkIcon, Icon } from "@chakra-ui/icons";
import { IoEyeOutline, IoEyeOffOutline } from "react-icons/io5";
import { useAuth } from "@/src/contexts/AuthContext";
import { useRouter } from "next/router";
import { useForm } from "react-hook-form";
import { validateEmail,validateGeneralEmail } from "@/src/utils/validate-functions";
import CustomLink from "@/src/components/CustomLink";
import useTranslation from "next-translate/useTranslation";

function Auth() {
  const { t } = useTranslation();
  const auth = useAuth();
  const router = useRouter();
  const [isLogin, setIsLogin] = React.useState(true);
  const [isRegister, setIsRegister] = React.useState(false);
  const [tabIndex, setTabIndex] = React.useState(isLogin ? 0 : 1);
  const allowed = ["manager", "collaborator"];
  const toast = useToast();
  const [show, setShow] = React.useState(false);
  const [successRegister, setSuccessRegister] = React.useState(null);
  const [errorLogin, setErrorLogin] = React.useState(null);
  const handleShowPassword = () => setShow(!show);
  const [loading, setLoading] = React.useState(false);
  const { colorMode } = useColorMode();
  const { redirect_url } = router.query;
  
  const {
    handleSubmit: handleSubmitLogin,
    register: registerLogin,
    formState: { errors: errorsLogin },
  } = useForm({ mode: "all" });

  const onLogin = async (data) => {
    setLoading(true);
    const resp = await auth.login(data);
    if (resp?.id) {
      setLoading(false);
      toast({
        position: "top-right",
        title: t("notification:toast.login.success.title"),
        description:
          auth?.authSuccess || t("notification:toast.login.success.message"),
        status: "success",
        duration: 8000,
        isClosable: true,
      });
      if(redirect_url){
        window.location.href=redirect_url
      }else{
        if(resp?.role=='administrator'){
          await router.push("/app/admin");
        }else{
          await router.push("/app/dashboard");
        }
      }
    } else {
      setLoading(false);
      setErrorLogin(resp?.data?.detail || auth?.authError?.detail);
    }
    setLoading(false);
  };

  return (
    <AuthShell>
      <AuthContent
        title={t("account:register.page.title")}
        sub_title={t("account:register.page.sub_title")}
        desc={t("account:register.page.description")}
      >
        <AuthCard
          title={t("account:register.card.welcome_text")}
          title_suffix={"Watchman"}
          isRegister={isRegister}
          isLogin={isLogin}
        >

          <Heading size={"lg"} color={colorMode === "light" ? "inherit" : "white"} mb={3}>
            {t("account:login.card.title")}
          </Heading>

          <Stack
          w={"full"}
            as={"form"}
            spacing={0}
            errors={errorsLogin}
            {...registerLogin}
            onSubmit={handleSubmitLogin((data) => onLogin(data))}
          >
            <FormControl
              isRequired
              isInvalid={errorsLogin?.email && errorsLogin?.email.message}
            >
              <FormLabel color={colorMode === "light" ? "inherit" : "white"}>
                {t("account:login.card.email.name")}
              </FormLabel>
              <Input
                borderColor={colorMode === "light" ? "inherit" : "brand.700"}
                bg={colorMode === "light" ? "white" : "transparent"}
                color={colorMode === "light" ? "inherit" : "white"}
                _placeholder={{
                  color: colorMode === "light" ? "inherit" : "gray.200",
                }}
                _hover={{
                  bgColor: colorMode === "light" ? "white" : "transparent",
                }}
                id="email"
                name={"email"}
                w={"full"}
                variant={"outline"}
                autoComplete={"username"}
                type={"text"}
                placeholder={t("account:login.card.email.placeholder")}
                {...registerLogin("email", {
                  required: t("common:validation.email_required"),
                  validate: (email) => {
                    return validateGeneralEmail(email, t);
                  },
                })}
              />
              {errorsLogin?.email ? (
                <FormErrorMessage mb={2}>
                  {errorsLogin?.email && errorsLogin?.email.message}
                </FormErrorMessage>
              ) : (
                <FormHelperText mb={2}>
                  {t("account:login.card.email.help")}
                </FormHelperText>
              )}
            </FormControl>
            <FormControl
              isRequired
              isInvalid={errorsLogin?.password && errorsLogin?.password.message}
            >
              <FormLabel color={colorMode === "light" ? "inherit" : "white"}>
                {t("account:login.card.password.name")}
              </FormLabel>
              <InputGroup size="md">
                <Input
                  borderColor={colorMode === "light" ? "inherit" : "brand.700"}
                  bg={colorMode === "light" ? "white" : "transparent"}
                  color={colorMode === "light" ? "inherit" : "white"}
                  _placeholder={{
                    color: colorMode === "light" ? "inherit" : "gray.200",
                  }}
                  _hover={{
                    bgColor: colorMode === "light" ? "white" : "transparent",
                  }}
                  id="password"
                  name={"password"}
                  w={"full"}
                  variant={"outline"}
                  autoComplete={"current-password"}
                  type={show ? "text" : "password"}
                  placeholder={t("account:login.card.password.placeholder")}
                  {...registerLogin("password", {
                    required: t("common:validation.password_required"),
                    minLength: {
                      value: 8,
                      message: t("common:validation.password_min_length"),
                    },
                  })}
                />
                <InputRightElement pt={2}>
                  <Icon
                    as={show ? IoEyeOutline : IoEyeOffOutline}
                    w={5}
                    h={5}
                    color={errorsLogin?.password ? "red.500" : "brand.500"}
                    onClick={handleShowPassword}
                  />
                </InputRightElement>
              </InputGroup>
              {errorsLogin?.password ? (
                <FormErrorMessage mb={1}>
                  {errorsLogin?.password && errorsLogin?.password.message}
                </FormErrorMessage>
              ) : (
                <FormHelperText mb={1}>
                  {t("account:login.card.password.help")}
                </FormHelperText>
              )}
              <Stack mb={5}>
                <CustomLink
                  fontSize={"sm"}
                  alignSelf={"flex-end"}
                  to={"/account/reset-password"}
                  color={"brand.500"}
                >
                  {t("common:forget_password")}
                </CustomLink>
              </Stack>
            </FormControl>
            <Button
              id="login"
              isLoading={loading}
              loadingText={t("common:loading")}
              w={"full"}
              fontWeight={500}
              type={"submit"}
              colorScheme={"brand"}
            >
              {t("account:login.card.button_text")}
            </Button>
            
          </Stack>
          {errorLogin && (
            <Alert status="error" my={4}>
              <AlertIcon />
              <Box>
                <AlertTitle
                  color={colorMode === "light" ? "inherit" : "red.300"}
                >
                  {t("common:error_login_title")}
                </AlertTitle>
                <AlertDescription
                  color={colorMode === "light" ? "inherit" : "white"}
                >
                  {errorLogin}
                </AlertDescription>
              </Box>
              <CloseButton
                alignSelf="flex-start"
                position="relative"
                right={-1}
                top={-1}
                onClick={() => setErrorLogin(null)}
              />
            </Alert>
          )}
          <Stack py={10} w="full" >
              <Text fontSize="sm" color={colorMode === "light" ? "inherit" : "white"} textAlign={"left"} >
              {t("account:login.card.no_account")}  {"  "}
              <CustomLink
                  fontSize={"sm"}
                  to={redirect_url==null || redirect_url=='' || redirect_url=='undefined' ? "/account/choose" : "/account/choose?redirect_url="+encodeURIComponent(redirect_url)}
                  color={"brand.500"}
                >
                 {t("account:register.card.title")}
                </CustomLink>
              </Text>
                
            </Stack>
        </AuthCard>
      </AuthContent>
    </AuthShell>
  );
}

const RegisterPage = () => (
  <Page name="Login" path="/account/login">
    <Auth />
  </Page>
);

export default RegisterPage;


    export async function getStaticProps(ctx) {
        
        
        return {
          
          
          props: {
            
            ...(await __loadNamespaces({
              ...ctx,
              pathname: '/account/login',
              loaderName: 'getStaticProps',
              ...__i18nConfig,
              loadLocaleFrom: (l, n) => import(`@next-translate-root/locales/${l}/${n}`).then(m => m.default),
            }))
          }
        }
    }
  